import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  App,
  Card,
  Col,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { useAuthContext } from 'hooks/useAuthContext';

import { ApiErrorData } from 'models/ApiResponse';
import { KitCampaign } from 'models/KitCampaign';
import { KitCompany } from 'models/KitCompany';
import {
  OperationBriefBlocks,
  OperationBriefForm,
} from 'models/OperationRequest';

import { KitCampaignService } from 'services/KitCampaignService';
import { KitCompanyService } from 'services/KitCompanyService';
import {
  PortalGoBriefCatalogs,
  PortalGoCatalogItem,
  PortalGoCatalogService,
} from 'services/PortalGoCatalogService';

import {
  OPS_GOALS,
  OPS_KAMS,
  OPS_PROFILES,
  OPS_TIME_OPTIONS,
  OPS_UNIFORMS,
  buildOpsWeeks,
  turnoHours,
} from '../data/operationCatalogs';

import './OpsBriefForm.scss';

interface OpsBriefFormProps {
  value: OperationBriefForm;
  onChange: (next: OperationBriefForm) => void;
}

const BLOCK_KEYS: (keyof OperationBriefBlocks)[] = ['am', 'pm', 'full'];

// Section 01 wraps by content: the campaign type never shrinks below its
// labels, so it drops to the KAM row when the first row runs out of room.
const WHO_FIELD_FLEX = '1 1 200px';
const WHO_TYPE_FLEX = '1 0 auto';
const WHO_KAM_FLEX = '1 1 300px';

const unwrapCampaignPayload = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as unknown[];
    if (Array.isArray(obj.items)) return obj.items as unknown[];
  }
  return [];
};

/** Acepta shape anidado `{ company: { id, name } }` o plano `{ companyId, companyName }`. */
const normalizeCampaigns = (raw: unknown): KitCampaign[] => {
  return unwrapCampaignPayload(raw)
    .map((item) => {
      const row = item as Record<string, any>;
      const id = Number(row?.id);
      if (!Number.isFinite(id)) return null;

      const companyIdRaw = row?.company?.id ?? row?.companyId;
      const companyId = Number(companyIdRaw);
      const companyName = String(row?.company?.name ?? row?.companyName ?? '');

      return {
        id,
        name: String(row?.name ?? ''),
        company: {
          id: Number.isFinite(companyId) ? companyId : -1,
          name:
            companyName ||
            (Number.isFinite(companyId) && companyId > 0
              ? String(companyId)
              : '—'),
        },
      } as KitCampaign;
    })
    .filter((item): item is KitCampaign => item !== null);
};

const mergeCampaigns = (...lists: KitCampaign[][]): KitCampaign[] => {
  const map = new Map<number, KitCampaign>();
  lists.flat().forEach((campaign) => {
    if (!map.has(campaign.id)) {
      map.set(campaign.id, campaign);
    }
  });
  return Array.from(map.values()).sort((a, b) =>
    a.name.localeCompare(b.name, 'es')
  );
};

/** Clientes solo desde campañas: el filtro Cliente→Campaña siempre matchea. */
const clientsFromCampaigns = (campaigns: KitCampaign[]): KitCompany[] => {
  const map = new Map<number, KitCompany>();
  campaigns.forEach((campaign) => {
    const id = Number(campaign.company?.id);
    if (!Number.isFinite(id) || id <= 0 || map.has(id)) return;
    map.set(id, {
      id,
      name: campaign.company?.name || String(id),
    });
  });
  return Array.from(map.values()).sort((a, b) =>
    a.name.localeCompare(b.name, 'es')
  );
};

const filterCampaignsByClient = (
  campaigns: KitCampaign[],
  clientId: number | null | undefined,
  clientName = ''
): KitCampaign[] => {
  if (clientId == null) return [];
  const id = Number(clientId);
  const name = clientName.trim().toLowerCase();

  return campaigns.filter((campaign) => {
    const campaignCompanyId = Number(campaign.company?.id);
    if (Number.isFinite(campaignCompanyId) && campaignCompanyId === id) {
      return true;
    }
    if (!name) return false;
    return (campaign.company?.name || '').trim().toLowerCase() === name;
  });
};

function FieldLabel({
  children,
  required,
}: {
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <label className="ops-brief-form__label">
      {children}
      {required && <span className="ops-brief-form__req">*</span>}
    </label>
  );
}

function FieldHint({ children }: { children: ReactNode }) {
  return <div className="ops-brief-form__hint">{children}</div>;
}

function OpsBriefForm({ value, onChange }: OpsBriefFormProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { user } = useAuthContext();
  const i18nBase =
    'scenes.requirements_operation.components.new_operation_request.brief';
  const errorsBase =
    'scenes.requirements_operation.components.new_operation_request.errors';

  const [clients, setClients] = useState<KitCompany[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<KitCampaign[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogs, setCatalogs] = useState<PortalGoBriefCatalogs>({
    kams: [],
    profiles: [],
    uniforms: [],
    goals: [],
  });
  const briefDefaultsApplied = useRef(false);
  const briefRef = useRef(value);
  briefRef.current = value;

  const patch = (partial: Partial<OperationBriefForm>) => {
    onChange({ ...value, ...partial });
  };

  const uniformValue = (item: PortalGoCatalogItem) =>
    item.code || String(item.id);

  useEffect(() => {
    let cancelled = false;

    const loadBriefCatalogs = async () => {
      setCatalogLoading(true);
      const response = await PortalGoCatalogService.getBriefCatalogs();
      if (cancelled) return;

      if (response.success) {
        const data = response.data as PortalGoBriefCatalogs;
        setCatalogs(data);

        if (!briefDefaultsApplied.current) {
          briefDefaultsApplied.current = true;
          const current = briefRef.current;
          const nextPatch: Partial<OperationBriefForm> = {};
          if (!current.kam && data.kams[0]) {
            nextPatch.kam = data.kams[0].label;
          }
          if (!current.profile && data.profiles[0]) {
            nextPatch.profile = data.profiles[0].label;
          }
          if (!current.goal && data.goals[0]) {
            nextPatch.goal = data.goals[0].label;
          }
          if (!current.uniform && data.uniforms[0]) {
            nextPatch.uniform = uniformValue(data.uniforms[0]);
          }
          if (Object.keys(nextPatch).length > 0) {
            onChange({ ...current, ...nextPatch });
          }
        }
      } else {
        // Fallback local si el BFF aún no tiene la tabla/seed.
        const fallback: PortalGoBriefCatalogs = {
          kams: OPS_KAMS.map((label, idx) => ({
            id: idx + 1,
            type: 'KAM',
            code: null,
            label,
            description: null,
            sortOrder: idx + 1,
            isActive: true,
          })),
          profiles: OPS_PROFILES.map((label, idx) => ({
            id: idx + 1,
            type: 'PROFILE',
            code: null,
            label,
            description: null,
            sortOrder: idx + 1,
            isActive: true,
          })),
          uniforms: OPS_UNIFORMS.map((u, idx) => ({
            id: idx + 1,
            type: 'UNIFORM',
            code: u.id,
            label: u.label,
            description: u.note,
            sortOrder: idx + 1,
            isActive: true,
          })),
          goals: OPS_GOALS.map((label, idx) => ({
            id: idx + 1,
            type: 'GOAL',
            code: null,
            label,
            description: null,
            sortOrder: idx + 1,
            isActive: true,
          })),
        };
        setCatalogs(fallback);

        if (!briefDefaultsApplied.current) {
          briefDefaultsApplied.current = true;
          const current = briefRef.current;
          onChange({
            ...current,
            kam: current.kam || fallback.kams[0]?.label || '',
            profile: current.profile || fallback.profiles[0]?.label || '',
            goal: current.goal || fallback.goals[0]?.label || '',
            uniform:
              current.uniform ||
              (fallback.uniforms[0] ? uniformValue(fallback.uniforms[0]) : ''),
          });
        }

        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${errorsBase}.fetch_catalogs`),
          3.5
        );
      }

      setCatalogLoading(false);
    };

    loadBriefCatalogs();

    return () => {
      cancelled = true;
    };
  }, [message, t, errorsBase, onChange]);

  useEffect(() => {
    let cancelled = false;

    const loadClientsAndCampaigns = async () => {
      const countryCode = user?.company?.countryCode || 'CL';
      const companyId = Number(user?.company?.id);

      setClientsLoading(true);
      setCampaignsLoading(true);

      // Campañas por país (mismo alcance que el catálogo de clientes KIT).
      // Merge con getByCurrentUser / getByCompany por si el país viene vacío.
      const campaignRequests: Promise<
        Awaited<ReturnType<typeof KitCampaignService.getByCountry>>
      >[] = [
        KitCampaignService.getByCountry(countryCode),
        KitCampaignService.getByCurrentUser(),
      ];

      if (Number.isFinite(companyId) && companyId > 0) {
        campaignRequests.push(KitCampaignService.getByCompany(companyId));
      }

      const [clientsResponse, ...campaignResponses] = await Promise.all([
        KitCompanyService.getByCountry(countryCode),
        ...campaignRequests,
      ]);

      if (cancelled) return;

      const lists: KitCampaign[][] = [];
      let campaignErrors = 0;
      let lastCampaignError: ApiErrorData | null = null;

      campaignResponses.forEach((response) => {
        if (response.success) {
          lists.push(normalizeCampaigns(response.data));
        } else {
          campaignErrors += 1;
          lastCampaignError = response.data as ApiErrorData;
        }
      });

      const campaignList = mergeCampaigns(...lists);
      setAllCampaigns(campaignList);

      if (campaignList.length === 0 && campaignErrors > 0) {
        message.error(
          ((lastCampaignError as ApiErrorData | null)?.message as string) ||
            t(`${errorsBase}.fetch_campaigns`),
          3.5
        );
      }

      let catalogClients: KitCompany[] = [];
      if (clientsResponse.success) {
        catalogClients = (
          Array.isArray(clientsResponse.data)
            ? (clientsResponse.data as KitCompany[])
            : []
        )
          .map((c) => ({
            id: Number(c.id),
            name: String(c.name || ''),
          }))
          .filter((c) => Number.isFinite(c.id) && c.id > 0)
          .sort((a, b) => a.name.localeCompare(b.name, 'es'));
      } else {
        const error = clientsResponse.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${errorsBase}.fetch_clients`),
          3.5
        );
      }

      const derivedClients = clientsFromCampaigns(campaignList);

      // Preferir clientes con al menos una campaña; si no hay match,
      // caer al catálogo / derivados para no dejar el select vacío.
      if (catalogClients.length > 0 && campaignList.length > 0) {
        const withCampaigns = catalogClients.filter((client) =>
          campaignList.some(
            (campaign) => Number(campaign.company?.id) === Number(client.id)
          )
        );
        setClients(withCampaigns.length > 0 ? withCampaigns : derivedClients);
      } else {
        setClients(catalogClients.length > 0 ? catalogClients : derivedClients);
      }

      setClientsLoading(false);
      setCampaignsLoading(false);
    };

    loadClientsAndCampaigns();

    return () => {
      cancelled = true;
    };
  }, [user?.company?.id, user?.company?.countryCode, message, t, errorsBase]);

  const campaignsForClient = useMemo(
    () => filterCampaignsByClient(allCampaigns, value.clientId, value.client),
    [allCampaigns, value.clientId, value.client]
  );

  const clientOptions = clients;

  const handleClientChange = (clientId: number) => {
    const selected = clientOptions.find(
      (c) => Number(c.id) === Number(clientId)
    );
    const clientName = selected?.name || '';
    const nextCampaigns = filterCampaignsByClient(
      allCampaigns,
      clientId,
      clientName
    );
    const firstCampaign = nextCampaigns[0];

    patch({
      clientId: Number(clientId),
      client: clientName,
      campaignId: firstCampaign ? Number(firstCampaign.id) : null,
      campaign: firstCampaign?.name || '',
    });
  };

  const handleCampaignChange = (campaignId: number) => {
    const selected = campaignsForClient.find(
      (c) => Number(c.id) === Number(campaignId)
    );
    patch({
      campaignId: Number(campaignId),
      campaign: selected?.name || '',
    });
  };

  const weeks = useMemo(() => buildOpsWeeks(), []);

  const selectedUniform = catalogs.uniforms.find(
    (u) => uniformValue(u) === value.uniform
  );

  const formatHours = (from: string, to: string) => {
    const h = turnoHours(from, to);
    return `${h % 1 ? h.toFixed(1) : h}h`;
  };

  return (
    <Card className="ops-brief-form" bordered>
      {/* 01 — ¿Para quién es? */}
      <section className="ops-brief-form__section">
        <header className="ops-brief-form__section-head">
          <span className="ops-brief-form__section-num">01</span>
          <div>
            <h3 className="ops-brief-form__section-title">
              {t(`${i18nBase}.sections.who.title`)}
            </h3>
            <p className="ops-brief-form__section-sub">
              {t(`${i18nBase}.sections.who.sub`)}
            </p>
          </div>
        </header>

        <Row gutter={[14, 14]}>
          <Col flex={WHO_FIELD_FLEX}>
            <FieldLabel required>{t(`${i18nBase}.labels.client`)}</FieldLabel>
            <Select
              className="ops-brief-form__control"
              showSearch
              optionFilterProp="label"
              loading={clientsLoading}
              placeholder={t(`${i18nBase}.placeholders.client`)}
              value={value.clientId ?? undefined}
              options={clientOptions.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
              onChange={handleClientChange}
            />
          </Col>
          <Col flex={WHO_FIELD_FLEX}>
            <FieldLabel required>{t(`${i18nBase}.labels.campaign`)}</FieldLabel>
            <Select
              className="ops-brief-form__control"
              showSearch
              optionFilterProp="label"
              loading={campaignsLoading}
              disabled={!value.clientId}
              placeholder={t(`${i18nBase}.placeholders.campaign`)}
              value={value.campaignId ?? undefined}
              options={campaignsForClient.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
              onChange={handleCampaignChange}
              notFoundContent={
                campaignsLoading
                  ? undefined
                  : t(`${i18nBase}.placeholders.campaign_empty`)
              }
            />
          </Col>
          <Col flex={WHO_FIELD_FLEX}>
            <FieldLabel required>{t(`${i18nBase}.labels.week`)}</FieldLabel>
            <Select
              className="ops-brief-form__control"
              value={value.week || undefined}
              options={weeks.map((w) => ({
                value: w.value,
                label: w.label,
              }))}
              onChange={(week) => patch({ week })}
            />
          </Col>
          <Col flex={WHO_TYPE_FLEX}>
            <FieldLabel required>{t(`${i18nBase}.labels.type`)}</FieldLabel>
            <Segmented
              block
              className="ops-brief-form__segmented"
              value={value.type}
              options={[
                {
                  value: 'contrato',
                  label: t(`${i18nBase}.types.contrato`),
                },
                { value: 'obra', label: t(`${i18nBase}.types.obra`) },
              ]}
              onChange={(type) =>
                patch({ type: type as OperationBriefForm['type'] })
              }
            />
            <FieldHint>{t(`${i18nBase}.helpers.type`)}</FieldHint>
          </Col>
          <Col flex={WHO_KAM_FLEX} className="ops-brief-form__kam-col">
            <FieldLabel required>{t(`${i18nBase}.labels.kam`)}</FieldLabel>
            <Select
              className="ops-brief-form__control"
              loading={catalogLoading}
              value={value.kam || undefined}
              options={catalogs.kams.map((k) => ({
                value: k.label,
                label: k.label,
              }))}
              onChange={(kam) => patch({ kam })}
            />
            <FieldHint>{t(`${i18nBase}.helpers.kam`)}</FieldHint>
          </Col>
        </Row>
      </section>

      {/* 02 — ¿Qué hará la persona? */}
      <section className="ops-brief-form__section">
        <header className="ops-brief-form__section-head">
          <span className="ops-brief-form__section-num">02</span>
          <div>
            <h3 className="ops-brief-form__section-title">
              {t(`${i18nBase}.sections.what.title`)}
            </h3>
            <p className="ops-brief-form__section-sub">
              {t(`${i18nBase}.sections.what.sub`)}
            </p>
          </div>
        </header>

        <Row gutter={[14, 14]}>
          <Col xs={24} md={8}>
            <FieldLabel required>{t(`${i18nBase}.labels.profile`)}</FieldLabel>
            <Select
              className="ops-brief-form__control"
              loading={catalogLoading}
              value={value.profile || undefined}
              options={catalogs.profiles.map((p) => ({
                value: p.label,
                label: p.label,
              }))}
              onChange={(profile) => patch({ profile })}
            />
          </Col>
          <Col xs={24} md={8}>
            <FieldLabel required>{t(`${i18nBase}.labels.uniform`)}</FieldLabel>
            <Select
              className="ops-brief-form__control"
              loading={catalogLoading}
              value={value.uniform || undefined}
              options={catalogs.uniforms.map((u) => ({
                value: uniformValue(u),
                label: u.label,
              }))}
              onChange={(uniform) => patch({ uniform })}
            />
            {selectedUniform?.description && (
              <FieldHint>{selectedUniform.description}</FieldHint>
            )}
          </Col>
          <Col xs={24} md={8}>
            <FieldLabel required>{t(`${i18nBase}.labels.goal`)}</FieldLabel>
            <Select
              className="ops-brief-form__control"
              loading={catalogLoading}
              value={value.goal || undefined}
              options={catalogs.goals.map((g) => ({
                value: g.label,
                label: g.label,
              }))}
              onChange={(goal) => patch({ goal })}
            />
          </Col>
          <Col xs={24}>
            <FieldLabel>{t(`${i18nBase}.labels.description`)}</FieldLabel>
            <Input.TextArea
              rows={4}
              value={value.description}
              placeholder={t(`${i18nBase}.placeholders.description`)}
              onChange={(e) => patch({ description: e.target.value })}
            />
            <FieldHint>{t(`${i18nBase}.helpers.description`)}</FieldHint>
          </Col>
        </Row>
      </section>

      {/* 03 — Pago y horarios */}
      <section className="ops-brief-form__section ops-brief-form__section--last">
        <header className="ops-brief-form__section-head">
          <span className="ops-brief-form__section-num">03</span>
          <div>
            <h3 className="ops-brief-form__section-title">
              {t(`${i18nBase}.sections.pay.title`)}
            </h3>
            <p className="ops-brief-form__section-sub">
              {t(`${i18nBase}.sections.pay.sub`)}
            </p>
          </div>
        </header>

        <Row gutter={[14, 14]}>
          <Col xs={24} sm={12} md={8} lg={5}>
            <FieldLabel required>{t(`${i18nBase}.labels.rate`)}</FieldLabel>
            <InputNumber
              className="ops-brief-form__control"
              min={0}
              step={1000}
              value={value.rate}
              addonBefore="CLP"
              onChange={(rate) => patch({ rate: Number(rate) || 0 })}
            />
            <FieldHint>{t(`${i18nBase}.helpers.rate`)}</FieldHint>
          </Col>
        </Row>

        <div className="ops-brief-form__blocks">
          {BLOCK_KEYS.map((key) => {
            const block = value.blocks[key];
            return (
              <div key={key} className="ops-brief-form__block">
                <div className="ops-brief-form__block-head">
                  <span className="ops-brief-form__block-tag">
                    {t(`${i18nBase}.blocks.${key}.tag`)}
                  </span>
                  <span className="ops-brief-form__block-name">
                    {t(`${i18nBase}.blocks.${key}.name`)}
                  </span>
                  <span className="ops-brief-form__block-hours">
                    {formatHours(block.from, block.to)}
                  </span>
                </div>
                <div className="ops-brief-form__block-times">
                  <Select
                    value={block.from}
                    options={OPS_TIME_OPTIONS.map((tm) => ({
                      value: tm,
                      label: tm,
                    }))}
                    onChange={(from) =>
                      patch({
                        blocks: {
                          ...value.blocks,
                          [key]: { ...block, from },
                        },
                      })
                    }
                  />
                  <span className="ops-brief-form__block-sep">→</span>
                  <Select
                    value={block.to}
                    options={OPS_TIME_OPTIONS.map((tm) => ({
                      value: tm,
                      label: tm,
                    }))}
                    onChange={(to) =>
                      patch({
                        blocks: {
                          ...value.blocks,
                          [key]: { ...block, to },
                        },
                      })
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </Card>
  );
}

export default OpsBriefForm;
